import { useMutation, useQuery } from '@tanstack/react-query'
import { Alert, Button, Collapse, DatePicker, Descriptions, Modal, Select, Space, Spin, Tag, Tooltip, Typography, message } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import React, { useEffect, useState } from 'react'
import { getCancellationRefundQuote, recordRefund, refundQueries } from '../../../../../Actions'
import {
  CancellationRefundQuote,
  Invoice,
  formatRatio,
  PENALTY_BRACKET_LABEL,
  PenaltyBracket,
  REFUND_REASON_FOLLOW_UP,
  REFUND_REASON_LABEL,
  RecordRefundRequest,
  RefundReason,
  resolveUsedAmount,
} from '../../../../../types/refund'
import { quoteOrReason } from '../../../../../utils/quoteError'
import useInvalidateRefunds from './useInvalidateRefunds'

type Props = {
  open: boolean
  workspaceId: number
  invoice: Invoice | null
  onClose: () => void
}

/**
 * 구독취소 산정에서 고를 수 있는 사유.
 *
 * 되감기 사유(`CONVERSION_*`)는 여기 없다 — 그쪽은 서버가 산정해 행을 만드는 별도 경로다
 * (`POST /v1/admin/refunds/rollback`, P4). 단건 크레딧·회사 귀책도 수동 기록 경로라 빠진다.
 * 사유가 곧 후속 조치를 정하므로 이 목록을 넓히면 엉뚱한 조치가 발화한다.
 */
const SELECTABLE_REASONS: RefundReason[] = ['SUBSCRIPTION_CANCEL', 'COOLING_OFF', 'TERMINATION_OPTION']

/**
 * 사유가 함의하는 위약금 구간. **판정이 아니라 대조용이다.**
 *
 * 구간 값 자체는 서버 산정이 내린 것을 그대로 싣고, 담당자가 사유를 바꿔 둘이 어긋날 때
 * 경고만 띄운다. 화면이 구간을 사유에 맞춰 고쳐 쓰면 위약금 **금액**은 산정값 그대로인 채
 * 근거 조항만 갈려 더 틀린 행이 된다 — 한 행 안에서 금액과 조항이 서로를 부정한다.
 */
const REASON_PENALTY_BRACKET: Partial<Record<RefundReason, PenaltyBracket>> = {
  SUBSCRIPTION_CANCEL: 'SUBSCRIPTION_CANCEL_FEE',
  COOLING_OFF: 'WITHIN_WITHDRAWAL',
  TERMINATION_OPTION: 'TERMINATION_OPTION',
}

const money = (value?: number | null) => (value == null ? '-' : `${Number(value).toLocaleString()}원`)

/**
 * 산정 미리보기 + 환불 접수 (화면 명세 2절 · aipark-front #266 P2)
 *
 * **미리보기는 아무것도 기록하지 않는다.** 행이 생기는 것은 `환불 접수` 를 누른 순간뿐이고,
 * 그때 산정값이 영구히 고정된다 — 접수 후에는 어떤 권한으로도 수정할 수 없고 정정은
 * 철회 후 재접수다.
 */
export default function RefundQuoteModal({ open, workspaceId, invoice, onClose }: Props) {
  const [referenceDate, setReferenceDate] = useState<Dayjs>(dayjs())
  const [reason, setReason] = useState<RefundReason | undefined>()
  /**
   * 정정 대상. 대장은 행을 지우지 않으므로 잘못 기입한 행은 철회해 두고 새 접수가 그것을
   * 가리킨다(화면 명세 8.4 · aivatar #502). **철회된 행**을 **같은 결제 건** 안에서만 지목할
   * 수 있고, 서버가 둘 다 400 으로 막는다.
   */
  const [supersedesRefundId, setSupersedesRefundId] = useState<number | undefined>()
  const invalidate = useInvalidateRefunds(workspaceId)

  const subscriptionId = invoice?.subscription?.subscriptionId ?? null

  // 모달을 다시 열 때 지난 기준일·사유가 남아 있으면 다른 결제 건에 그 값이 적용된다
  useEffect(() => {
    if (open) {
      setReferenceDate(dayjs())
      setReason(undefined)
      setSupersedesRefundId(undefined)
    }
  }, [open, invoice?.id])

  const isoDate = referenceDate.format('YYYY-MM-DD')

  const { data: result, isFetching } = useQuery({
    // 기준일이 키에 들어간다 — 날짜를 바꾸면 다시 산정한다.
    // gcTime 0: 모달을 닫았다 열면 낡은 산정이 보이지 않아야 한다.
    queryKey: ['refund-quote', 'cancellation', subscriptionId, isoDate],
    queryFn: () => quoteOrReason<CancellationRefundQuote>(() => getCancellationRefundQuote(subscriptionId!, isoDate)),
    enabled: open && subscriptionId != null,
    gcTime: 0,
    retry: false,
  })

  const quote = result?.ok ? result.data : null

  /**
   * 정정 후보를 담은 청구서는 **`quote.paidInvoiceId`** 다 — 담당자가 클릭한 행이 아니다.
   * 접수 행이 붙는 곳과 같아야 한다(아래 `submit` 주석). 다른 청구서의 철회 행을 지목하면
   * 서버가 400 으로 막으므로 목록 자체를 그 규칙으로 좁힌다.
   */
  const targetInvoiceId = quote?.paidInvoiceId ?? null

  const { data: targetView } = useQuery({
    ...refundQueries.invoiceView(targetInvoiceId ?? 0),
    enabled: open && targetInvoiceId != null,
  })

  const withdrawnRefunds = (targetView?.refunds ?? []).filter((refund) => refund.status === 'WITHDRAWN')

  // 기준일을 바꿔 대상 청구서가 달라지면 지난 선택은 남의 청구서 행이 된다
  useEffect(() => {
    setSupersedesRefundId(undefined)
  }, [targetInvoiceId])

  // 사유 기본값은 서버 판정을 따른다. 청약철회 기간 이내면 위약금이 0 이고 근거 조항이 다르다
  useEffect(() => {
    if (quote && !reason) {
      setReason(quote.withinWithdrawalPeriod ? 'COOLING_OFF' : 'SUBSCRIPTION_CANCEL')
    }
  }, [quote, reason])

  const { mutateAsync: receive, isPending } = useMutation({
    mutationFn: ({ invoiceId, body }: { invoiceId: number; body: RecordRefundRequest }) =>
      recordRefund(invoiceId, body),
  })

  /**
   * 접수 대상은 **`quote.paidInvoiceId`** 다. 담당자가 클릭한 행이 아니다.
   *
   * 서버가 그 주기의 성공 결제를 찾아 지목해 준다. 손으로 고르면 지난 주기 인보이스에 환불을
   * 붙이게 되고, 과다 환불 가드(합계 ≤ 결제액)가 엉뚱한 금액을 기준으로 걸린다.
   */
  const submit = async () => {
    if (!quote || !reason || quote.paidInvoiceId == null || quote.amount == null) return

    await receive({
      invoiceId: quote.paidInvoiceId,
      body: {
        status: 'QUOTED',
        // 🔴 산정값을 가공하지 않는다. 서버는 접수 시 산정을 다시 하지 않으므로
        // 여기서 반올림하거나 다시 계산하면 근거와 금액이 갈린다(aivatar #488 (b))
        amount: quote.amount,
        penalty: quote.penalty,
        reason,
        calculation: quote.calculation,
        /**
         * 위약금의 **근거**. 금액만 남기면 수수료 0% 가 나오는 조항이 셋이라(청약철회 기간
         * 이내 · 종료 선택권 · 회사 귀책) 사후에 어느 조항이었는지 되짚을 수 없다(D-66).
         *
         * 🔴 `?? undefined` 여야 한다 — 요율 `0` 은 버릴 값이 아니라 **보낼 값**이다. 단위
         * 변환도 하지 않는다: 산정과 같은 비율(0.1 = 10%)로 넘기고 서버가 퍼센트로 바꿔 저장한다.
         */
        penaltyBracket: quote.penaltyBracket ?? undefined,
        penaltyRate: quote.penaltyRate ?? undefined,
        supersedesRefundId,
      },
    })

    message.success('환불을 접수했습니다. 결제대행사에서 취소를 실행한 뒤 처리 완료를 확정하세요.')
    await invalidate()
    onClose()
  }

  /** 기준일이 오늘이 아니면 한 번 더 묻는다 — 산정이 이 날짜로 영구히 고정된다 */
  const confirmAndSubmit = () => {
    const stale = !referenceDate.isSame(dayjs(), 'day')
    const otherInvoice = quote?.paidInvoiceId != null && quote.paidInvoiceId !== invoice?.id

    if (!stale && !otherInvoice) {
      submit()
      return
    }

    Modal.confirm({
      title: '이대로 접수할까요?',
      content: (
        <Space direction="vertical">
          {stale ? (
            <span>
              산정 기준일이 오늘이 아닌 <b>{isoDate}</b> 입니다. 접수하면 이 날짜의 산정으로 고정되며 수정할 수
              없습니다.
            </span>
          ) : null}
          {otherInvoice ? (
            <span>
              환불 기록은 서버가 지목한 결제 건 <b>#{quote?.paidInvoiceId}</b> 에 붙습니다 (클릭한 행은 #
              {invoice?.id}).
            </span>
          ) : null}
        </Space>
      ),
      okText: '접수',
      cancelText: '취소',
      onOk: submit,
    })
  }

  const usedAmount = quote ? resolveUsedAmount(quote) : null
  /** 결제 없이 만들어진 구독. 금액이 정가 폴백이라 그대로 환불하면 받은 적 없는 돈이 나간다 */
  const noPayment = !!quote && quote.paidInvoiceId == null

  /**
   * 고른 사유와 산정이 내린 구간의 어긋남. 담당자가 기본값에서 사유를 바꾸면 생긴다.
   *
   * **막지 않고 알리기만 한다.** 회사 귀책처럼 운영 판단으로 사유를 바꾸는 건이 있고, 그렇다고
   * 조용히 보내면 이 이슈가 없애려는 "사후에 조항을 되짚을 수 없다" 가 다른 모양으로 남는다.
   */
  const expectedBracket = reason ? REASON_PENALTY_BRACKET[reason] : undefined
  const bracketMismatch =
    !!quote?.penaltyBracket && !!expectedBracket && quote.penaltyBracket !== expectedBracket

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={720}
      title="환불 산정 미리보기"
      footer={[
        <Button key="close" onClick={onClose}>
          닫기
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={isPending}
          disabled={!quote || !reason || noPayment || !quote.amount}
          onClick={confirmAndSubmit}
        >
          환불 접수
        </Button>,
      ]}
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="이 화면은 아무것도 기록하지 않습니다"
        description="행이 생기는 것은 '환불 접수' 를 누른 순간이며, 그때 산정값이 고정되어 이후 수정할 수 없습니다."
      />

      <Space style={{ marginBottom: 12 }}>
        <span>산정 기준일 (해지 신청일)</span>
        <DatePicker
          allowClear={false}
          value={referenceDate}
          onChange={(value) => value && setReferenceDate(value)}
        />
      </Space>

      <Spin spinning={isFetching}>
        {result && !result.ok ? (
          // 400 은 오류가 아니라 "산정할 수 없다 + 이유" 다. 원문을 그대로 띄운다 —
          // 다음에 무엇을 해야 하는지가 문장 안에 들어 있다
          <Alert type="warning" showIcon message="산정할 수 없습니다" description={result.reason} />
        ) : null}

        {noPayment ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 12 }}
            message="이 주기에 성공한 결제가 없습니다"
            description="금액이 실결제액이 아니라 플랜 정가 폴백입니다. 그대로 환불하면 받은 적 없는 돈이 나갑니다. 접수를 막아두었습니다."
          />
        ) : null}

        {quote ? (
          <>
            <Descriptions title="대상 결제 건" size="small" column={2} bordered style={{ marginBottom: 12 }}>
              <Descriptions.Item label="플랜">{quote.planName ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="주기">
                {quote.intervalMonth === 1 ? '월 구독' : `${quote.intervalMonth ?? '-'}개월`}
              </Descriptions.Item>
              <Descriptions.Item label="결제일">{quote.paidOn ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="청구액">{money(quote.charge)}</Descriptions.Item>
              <Descriptions.Item label="결제 고유번호">{invoice?.paymentNumber ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="주문번호">{invoice?.orderNumber ?? '-'}</Descriptions.Item>
            </Descriptions>

            <Descriptions title="산정 결과" size="small" column={2} bordered style={{ marginBottom: 12 }}>
              <Descriptions.Item label="청약철회 기간">
                {quote.withinWithdrawalPeriod ? <Tag color="green">이내</Tag> : <Tag>지남</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="산정 기준일">{quote.referenceDate}</Descriptions.Item>
              <Descriptions.Item label="사용 개월수">{quote.usedMonths ?? '-'}</Descriptions.Item>
              <Descriptions.Item
                label={
                  <Tooltip title="서버 응답에 없는 값이라 기준 청구액 − 수수료 공제 전 환불액으로 역산했습니다. 월 구독 산정은 MIN(기간 축, 크레딧 축)이라 크레딧 축이 이기는 건에서는 시간 사용분과 다를 수 있습니다. 근거는 아래 '산정 재료 > 산정 내역' 의 서버 문장입니다">
                    사용분 금액 (역산)
                  </Tooltip>
                }
              >
                {money(usedAmount)}
              </Descriptions.Item>
              <Descriptions.Item label="위약금 구간">
                {quote.penaltyBracket ? PENALTY_BRACKET_LABEL[quote.penaltyBracket] : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="취소 수수료">
                {money(quote.penalty)} ({formatRatio(quote.penaltyRate)})
              </Descriptions.Item>
              <Descriptions.Item label="최종 환불액 (부가세 포함)">
                <Typography.Text strong>{money(quote.amount)}</Typography.Text>
              </Descriptions.Item>
              <Descriptions.Item label="부가세 제외">{money(quote.amountExcludingVat)}</Descriptions.Item>
            </Descriptions>

            <Collapse
              size="small"
              style={{ marginBottom: 12 }}
              items={[
                {
                  key: 'materials',
                  label: '산정 재료',
                  children: (
                    <Descriptions size="small" column={2} bordered>
                      <Descriptions.Item label="기준 청구액">{money(quote.refundBasis)}</Descriptions.Item>
                      <Descriptions.Item label="실결제액">{money(quote.charge)}</Descriptions.Item>
                      <Descriptions.Item label="지급 크레딧">{quote.creditGrant ?? '-'}</Descriptions.Item>
                      <Descriptions.Item label="사용 크레딧">{quote.creditConsumed ?? '-'}</Descriptions.Item>
                      <Descriptions.Item label="크레딧 사용률">{formatRatio(quote.creditUsedRatio)}</Descriptions.Item>
                      <Descriptions.Item label="첫 주기 합산 달">
                        {quote.transitionMonth ? '예' : '아니오'}
                      </Descriptions.Item>
                      <Descriptions.Item label="경과일 비율(연)">{formatRatio(quote.elapsedRatio)}</Descriptions.Item>
                      <Descriptions.Item label="잔여 비율(월)">{formatRatio(quote.remainingRatio)}</Descriptions.Item>
                      <Descriptions.Item label="수수료 공제 전">{money(quote.beforeFee)}</Descriptions.Item>
                      <Descriptions.Item label="대상 결제 건 id">{quote.paidInvoiceId ?? '-'}</Descriptions.Item>
                      <Descriptions.Item label="산정 내역" span={2}>
                        {/* 금액이 어떻게 나왔는지에 대한 서버의 설명이다. 화면 계산으로 대체하지 않는다 */}
                        {quote.calculation ?? '-'}
                      </Descriptions.Item>
                    </Descriptions>
                  ),
                },
              ]}
            />

            <Space direction="vertical" style={{ width: '100%' }}>
              <span>
                환불 사유 <Typography.Text type="danger">*</Typography.Text>
              </span>
              <Select
                style={{ width: '100%' }}
                placeholder="사유를 고르세요"
                value={reason}
                onChange={setReason}
                options={SELECTABLE_REASONS.map((value) => ({
                  value,
                  label: `${REFUND_REASON_LABEL[value]} — ${REFUND_REASON_FOLLOW_UP[value]}`,
                }))}
              />
              {/* 사유가 후속 조치를 정한다. 담당자가 조치를 따로 고르지 않으므로 무엇이
                  일어날지 여기서 보여준다 */}
              {reason ? (
                <Typography.Text type="secondary">
                  처리 완료를 확정하면 <b>{REFUND_REASON_FOLLOW_UP[reason]}</b> 이(가) 실행됩니다.
                </Typography.Text>
              ) : null}

              {bracketMismatch ? (
                <Alert
                  type="warning"
                  showIcon
                  message="고른 사유와 산정이 내린 위약금 구간이 어긋납니다"
                  description={`산정 구간은 "${PENALTY_BRACKET_LABEL[quote.penaltyBracket!]}" 이고 위약금은 ${money(quote.penalty)} 입니다. 접수하면 구간·금액이 산정값 그대로 박히므로, 사유만 바뀐 행은 근거 조항과 금액이 서로 어긋난 채 남습니다.`}
                />
              ) : null}

              {/* 구간이 비면 대장의 근거 칸이 영구히 빈다 — 접수는 되돌릴 수 없으므로 미리 알린다 */}
              {quote.penaltyBracket == null ? (
                <Typography.Text type="warning">
                  산정이 위약금 구간을 내려주지 않았습니다 — 대장의 구간 칸이 빈 채로 남습니다.
                </Typography.Text>
              ) : null}

              <span>정정 대상 (선택)</span>
              <Select
                allowClear
                style={{ width: '100%' }}
                placeholder={
                  withdrawnRefunds.length
                    ? '이 접수가 정정하는 원 환불 기록'
                    : `청구서 #${targetInvoiceId} 에 철회된 기록이 없습니다`
                }
                disabled={!withdrawnRefunds.length}
                value={supersedesRefundId}
                options={withdrawnRefunds.map((refund) => ({
                  value: refund.id,
                  label: `#${refund.id} · ${money(refund.amount)} · ${REFUND_REASON_LABEL[refund.reason] ?? refund.reason}`,
                }))}
                onChange={setSupersedesRefundId}
              />
              {/* 행을 지우지 않는 대장에서 "어느 쪽이 유효한 기록인가" 를 잇는 유일한 값이다 */}
              <Typography.Text type="secondary">
                잘못 기입한 행은 철회해 두고 여기서 지목하면 대장과 상세에서 두 행이 서로를
                가리킵니다. 정정이 아닌 신규 접수면 비워 두세요.
              </Typography.Text>
            </Space>
          </>
        ) : null}
      </Spin>
    </Modal>
  )
}
